/* TheKaizenCabinet — Public Landing Page
 *
 * Sections:
 *   1. Hero with whimsical typewriter + animated mesh background
 *   2. Feature cards (flip on click → witty backside)
 *   3. Vertical tool tabs (left list, right detail + "what if")
 *   4. Principles & ethics
 *   5. Animated consultant workflow (interactive)
 *   6. CTA — hello@krri.sh
 *
 * All Bricolage Grotesque headings, sage-themed, no stock photos.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import {
  Leaf, ArrowRight, Mail, Sparkles, Server, Lock, Shield, Eye, Users, Wrench,
  GitBranch, BarChart3, FileText, ListChecks, Target, Layers, BookOpen, Zap,
  CheckCircle, ChevronRight, Cpu, Palette, Building2, Rocket, Compass, Database,
} from "lucide-react";
import { TOOL_DOCS } from "../docs/toolDocs";

// ─────────────────────── Hero typewriter ───────────────────────
const HERO_LINES = [
  "39 lean tools. One quiet little cabinet.",
  "Diagnose like Toyota, deliver like a startup.",
  "Spreadsheets retire. Standard work begins.",
  "Find the waste. Wear it as a trophy.",
  "Where consultants keep their best playbooks.",
];

function TypewriterHeadline() {
  const [i, setI] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | pausing | erasing
  useEffect(() => {
    const full = HERO_LINES[i];
    let timeout;
    if (phase === "typing") {
      if (text.length < full.length) timeout = setTimeout(() => setText(full.slice(0, text.length + 1)), 38);
      else timeout = setTimeout(() => setPhase("pausing"), 1700);
    } else if (phase === "pausing") {
      timeout = setTimeout(() => setPhase("erasing"), 1500);
    } else if (phase === "erasing") {
      if (text.length > 0) timeout = setTimeout(() => setText(text.slice(0, text.length - 1)), 18);
      else { setI((i + 1) % HERO_LINES.length); setPhase("typing"); }
    }
    return () => clearTimeout(timeout);
  }, [text, phase, i]);
  return (
    <span className="inline">
      <span data-testid="hero-typewriter">{text}</span>
      <span className="inline-block w-[3px] h-[0.9em] bg-sage-700 ml-1 align-baseline animate-pulse" />
    </span>
  );
}

// ─────────────────────── Animated mesh background ───────────────────────
function MeshBg() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-32 -left-20 w-[28rem] h-[28rem] rounded-full bg-sage-300/30 blur-[120px] animate-blob" />
      <div className="absolute top-1/3 right-0 w-[26rem] h-[26rem] rounded-full bg-amber-200/30 blur-[120px] animate-blob animation-delay-2000" />
      <div className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-emerald-200/40 blur-[110px] animate-blob animation-delay-4000" />
      {/* Subtle grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.07]" aria-hidden>
        <defs>
          <pattern id="lp-grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M48 0H0V48" fill="none" stroke="#3F4D40" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lp-grid)" />
      </svg>
    </div>
  );
}

// ─────────────────────── Features (flip cards) ───────────────────────
const FEATURES = [
  { icon: Wrench, title: "39 Lean wizards", body: "VSM, 5S, RCA, PFMEA, Kaizen, Erlang C, ROI, ADKAR — the works.", joke: "Yes thirty-nine. We counted twice." },
  { icon: BarChart3, title: "Staffing diagnostic", body: "Bottom-up FTE model with workforce intel and roadmap.", joke: "Tells you who's overbooked and who's overcaffeinated." },
  { icon: GitBranch, title: "Visual VSM builder", body: "Matrix lanes, pain points and a density heatmap on one canvas.", joke: "Like LEGO for processes — except adults are encouraged." },
  { icon: ListChecks, title: "Standard Work + LSW", body: "Author SOPs, schedule Layered Audits and Leader routines.", joke: "Because chaos is a flavour, not a strategy." },
  { icon: Building2, title: "Multi-company portfolio", body: "Slot-based wallet economy keeps consultants organised.", joke: "Toggle between clients without forgetting whose ROI is whose." },
  { icon: Compass, title: "Workflow journey", body: "Curated 17-step path from scoping to sustainment.", joke: "It's a treasure map, but the treasure is FTEs you can redeploy." },
  { icon: Layers, title: "Cross-tool insights", body: "Spots gaps — Pareto without RCA, VSM without Time Study, etc.", joke: "It's the assistant that side-eyes you when steps are missing." },
  { icon: BookOpen, title: "39 in-depth docs", body: "Purpose, history, how-to, examples, pitfalls — for every tool.", joke: "A handbook your senior consultant promised but never wrote." },
  { icon: FileText, title: "Whitelabel PDF/Excel", body: "Branded exports for client deliverables, with logo and colours.", joke: "Make your reports look like a Big-4 invoice. Without the invoice." },
  { icon: Users, title: "Client portal", body: "Read-only tokenised links so customers can review safely.", joke: "Your client logs in. Their feedback comes back. Pure magic." },
  { icon: Target, title: "Benchmark companies", body: "Side-by-side coverage, journey % and signature KPIs.", joke: "League tables, but for grown-up operations." },
  { icon: Server, title: "Self-hosted by default", body: "Runs on your stack, on your terms. No phone-home.", joke: "Your data stays in your house. We don't even know your Wi-Fi." },
];

function FlipCard({ f }) {
  const [flipped, setFlipped] = useState(false);
  const Icon = f.icon;
  return (
    <button
      data-testid={`feature-card-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
      onClick={() => setFlipped(!flipped)}
      className="group relative h-44 w-full perspective-1000 text-left"
      aria-label={`Toggle ${f.title}`}
    >
      <div className={`absolute inset-0 transition-transform duration-500 transform-style-preserve-3d ${flipped ? "rotate-y-180" : ""}`}>
        {/* Front */}
        <div className="absolute inset-0 backface-hidden bg-white border border-sage-200 rounded-md p-4 shadow-sm hover:shadow-md hover:border-sage-400 transition-all">
          <div className="w-9 h-9 bg-sage-100 text-sage-700 rounded-md flex items-center justify-center group-hover:bg-sage-600 group-hover:text-white transition-colors mb-3">
            <Icon className="w-4 h-4" />
          </div>
          <p className="font-heading text-base font-semibold text-sage-900 leading-tight">{f.title}</p>
          <p className="text-xs text-sage-500 mt-1 leading-relaxed">{f.body}</p>
          <span className="absolute bottom-3 right-3 text-[10px] text-sage-400 italic">tap for the joke →</span>
        </div>
        {/* Back */}
        <div className="absolute inset-0 backface-hidden rotate-y-180 bg-sage-700 text-cream rounded-md p-4 shadow-md flex flex-col justify-center">
          <Sparkles className="w-4 h-4 text-amber-300 mb-2" />
          <p className="font-heading text-base font-semibold leading-snug">{f.joke}</p>
          <span className="absolute bottom-3 right-3 text-[10px] text-cream/60 italic">tap to flip back</span>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────── Vertical tools tabs ───────────────────────
// Per-tool USP — each one a distinctive selling point for THIS tool, not generic.
const TOOL_USPS = {
  staffing_assessment: "The only FTE model on the market that ingests local + expat working hours, yield, and Emiratisation/localisation targets in one go — no more spreadsheets glued together with VLOOKUPs.",
  vsm_builder: "Matrix lanes (phase × performer) with a live density heat-map make pain-points jump off the canvas — most VSM tools still draw flat ribbons.",
  sipoc_mapper: "Forces the supplier-and-customer columns to balance — flags scope creep on day one of an engagement, before the hours bleed.",
  voc_kano: "Auto-ranks Customer requirements by opportunity gap (importance × satisfaction shortfall) AND Kano category — turns 200 verbatim quotes into a prioritised backlog.",
  time_study: "Computes mean / range / CV % per activity automatically — high-CV stages light up as standardisation candidates without you running the maths.",
  waste_walk: "8-waste heat-map by location + photo evidence + cost estimate per observation — the only walk template that hands you a kaizen backlog at the end.",
  rca_5whys_fishbone: "Couples 6M brainstorming with structured 5-Why drills on the same canvas — most tools force you to pick one or the other.",
  pareto_analyzer: "Auto-classifies the Vital Few (≤80 %) vs Trivial Many in real time as you type — and re-Paretos the residual when the top causes are addressed.",
  spaghetti_diagram: "Plots multiple operator paths on one layout with auto-distance maths — pre/post comparisons need just two saved diagrams to quantify metres saved.",
  pfmea: "Side-by-side current and post-action RPN with a 'risk-saved' KPI — sponsors finally see what the action plan is worth in basis points.",
  a3_problem_solving: "Eight boxes with a completion meter coaching the consultant on weak thinking — replaces a 30-slide deck with a single signed page.",
  dmaic_charter: "Phase-gated deliverables with progress bars per phase — flags the project that is sneaking past Measure-stage MSA before Improve starts.",
  yamazumi: "Live SVG stacked bars vs Takt with bottleneck / under-loaded badges — element-level rebalancing happens by clicking, not by re-drawing the chart.",
  impact_effort: "Tinted 2×2 with auto-quadrant placement and per-quadrant grouped lists — replaces 90 minutes of post-it-note debate.",
  kaizen_charter: "Pre-event briefing + after-action report + 30-day follow-up baked into one artefact — kaizen events that actually stick beyond Friday afternoon.",
  lean_wave_charter: "Multi-quarter wave roadmap with milestone-level RAG and benefit roll-up — programme governance for transformations that outlast a single Kaizen.",
  roi_calculator: "Discounted cash-flow with NPV / ROI / payback + sensitivity table — finance signs off the business case in one meeting, not five.",
  idea_bank: "Status-driven funnel (Submitted → Approved → Done) + benefit roll-up — celebrates breadth, not just headline ideas, sustaining frontline engagement.",
  standard_work: "Step-by-step SWD with sequence, takt and key-points — postable as a wall poster the same day a Kaizen ends.",
  poka_yoke: "Catalogues prevention vs detection devices with coverage % and cost — the only place a CFO and a QC engineer can have the same conversation.",
  kanban_designer: "Cards-from-demand maths + per-stage WIP limits — prevents the over-production waste that even Lean tooling tends to ignore.",
  smed: "Internal vs External step classification with conversion logic baked in — turns a 4-hour changeover into < 10 min using Shingo's original method.",
  kpi_scorecard: "Tiered Tier-1/2/3 metrics with named owners and RAG bands — kills 'wallpaper dashboards' nobody updates.",
  tier_meeting: "Per-tier agenda with time-boxes + explicit escalation triggers — daily-management discipline you can hand to a new supervisor day-1.",
  leader_standard_work: "Codifies a leader's day with Gemba %, coaching dialogues and audit cadences — the single biggest predictor of sustained transformation.",
  lpa_scheduler: "Multi-layer audit calendar with rotating questions and compliance % trend — the IATF-grade quality discipline most ops teams skip.",
  benefit_tracker: "Committed vs actual savings per period with attainment % — what the CFO will look at when she asks 'did the programme actually pay back?'",
  iluo_matrix: "Clickable I/L/U/O cells with versatility % per person and coverage % per skill — single-point-of-failure risk surfaces automatically.",
  tna: "Capability-vs-demand FTE gap with criticality flags — links workforce planning directly to L&D budget without an HR consultant in between.",
  adkar: "Per-stakeholder heat-map across the 5 gates — surfaces the weakest gate per group so change-comms become surgical, not generic.",
  lean_academy: "Belt-pipeline funnel + project tracker — turns Lean training spend into validated, finance-confirmed savings per learner.",
  erlang_c: "Interval-based agent sizing with shrinkage-grossed FTE — the only call-centre formula contact-centre auditors will accept in a board pack.",
  monte_carlo_queue: "Stochastic queue simulation when arrival/service distributions break Erlang's assumptions — finds the SL knee-point Erlang misses.",
  attrition_cohort: "Cohort-based retention curves + hiring-wave + shock scenarios — the only workforce model that catches the Q3 capacity dip before it bites.",
  automation_scorer: "Multi-criteria weighted scoring of RPA/AI/BPM candidates — kills 'bot-stamp-collecting' before the platform team is over-extended.",
  customer_journey: "Stage × touchpoint emotion curve (-2 to +2) — pinpoints the single moment driving NPS drops with surgical precision.",
  dilo: "Sequential VA/NVA/BNVA/Wait time-log per role — the manager's 41 %-in-admin reality check no spans-and-layers project should skip.",
  five_s_audit: "Domain-specific 5S templates (banking, healthcare, BPO, telecom…) with remediation tracker and trend over time — Sustain becomes measurable, not aspirational.",
  maturity_assessment: "Customisable pillars graded across 5 levels with radar comparison and gap-sorted action backlog — the multi-year roadmap built on data, not opinion.",
};

function ToolsTabs() {
  const [active, setActive] = useState(TOOL_DOCS[0].id);
  const tool = useMemo(() => TOOL_DOCS.find(t => t.id === active), [active]);
  const Icon = tool ? (Icons[tool.icon] || Icons.Wrench) : Icons.Wrench;
  const usp = TOOL_USPS[active] || tool.tagline;

  return (
    <div className="grid grid-cols-12 gap-4 bg-white border border-[#E2E8E3] rounded-lg overflow-hidden" data-testid="landing-tools-tabs">
      {/* Left list — scrollable */}
      <div className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-sage-100 bg-sage-50/40 max-h-[50vh] overflow-y-auto">
        {TOOL_DOCS.map((t) => {
          const isOn = active === t.id;
          const I = Icons[t.icon] || Icons.Wrench;
          return (
            <button
              key={t.id}
              data-testid={`landing-tool-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm border-l-2 transition-all ${
                isOn ? "border-sage-600 bg-white text-sage-900 font-semibold" : "border-transparent text-sage-600 hover:bg-white/70"
              }`}
            >
              <I className={`w-3.5 h-3.5 ${isOn ? "text-sage-700" : "text-sage-400"}`} />
              <span className="truncate">{t.name}</span>
            </button>
          );
        })}
      </div>
      {/* Right panel — half-page height with scroll */}
      <div className="col-span-12 md:col-span-8 lg:col-span-9 max-h-[50vh] overflow-y-auto p-6 animate-fade-in" key={active}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-sage-600 text-white rounded-md flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-sage-500 font-semibold">{tool.category}</p>
            <h3 className="font-heading text-xl font-semibold text-sage-900 leading-tight">{tool.name}</h3>
            <p className="text-sage-600 text-sm mt-1 italic">{tool.tagline}</p>
          </div>
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-sage-500 font-semibold mb-1">How you'll use it</p>
            <ol className="list-decimal pl-4 text-sm text-sage-700 space-y-1.5">
              {tool.howToUse.slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-sage-500 font-semibold mb-1">What you'll get out</p>
            <ul className="list-disc pl-4 text-sm text-sage-700 space-y-1.5">
              {tool.outputs.slice(0, 4).map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </div>
        </div>
        <div className="mt-4 bg-amber-50 border-l-2 border-amber-400 p-3 rounded-r-md" data-testid={`tool-usp-${active}`}>
          <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-0.5 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Tool USP
          </p>
          <p className="text-xs text-amber-900 italic leading-relaxed">{usp}</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────── Workflow steps ───────────────────────
const WORKFLOW = [
  { icon: Building2, title: "Onboard", body: "Create the company, set domain, currency and localisation target." },
  { icon: Compass,   title: "Scope",   body: "SIPOC + VOC/Kano define the shape of the engagement." },
  { icon: Eye,       title: "Diagnose",body: "VSM, Time Study, Pareto, Waste Walk surface the truth." },
  { icon: Target,    title: "Design",  body: "RCA, PFMEA, A3, Yamazumi, Impact×Effort shape the redesign." },
  { icon: Rocket,    title: "Implement",body: "Kaizen, ROI, Standard Work, Kanban, SMED bring the change." },
  { icon: Shield,    title: "Sustain", body: "5S, KPI, LSW, LPA, Benefit Tracker — gains compound." },
];

function AnimatedWorkflow() {
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const id = setInterval(() => setActive(a => (a + 1) % WORKFLOW.length), 2400);
    return () => clearInterval(id);
  }, []);
  return (
    <div ref={ref} className="bg-white border border-sage-100 rounded-lg p-6" data-testid="landing-workflow">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-2xl font-semibold text-sage-900">The consultant journey</h3>
        <p className="text-xs text-sage-500">click a step or watch it cycle</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
        {WORKFLOW.map((w, i) => {
          const Icon = w.icon;
          const isOn = active === i;
          return (
            <button
              key={w.title}
              onClick={() => setActive(i)}
              data-testid={`workflow-step-${w.title.toLowerCase()}`}
              className={`group relative flex flex-col items-center justify-center rounded-md p-3 border-2 transition-all ${
                isOn
                  ? "border-sage-600 bg-sage-50 -translate-y-1 shadow-sm"
                  : "border-sage-100 bg-white hover:border-sage-300"
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                isOn ? "bg-sage-600 text-white" : "bg-sage-100 text-sage-600 group-hover:bg-sage-200"
              }`}>
                <Icon className="w-4 h-4" />
              </div>
              <p className={`mt-2 text-xs font-semibold ${isOn ? "text-sage-900" : "text-sage-600"}`}>{w.title}</p>
              <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-sage-200 text-[10px] flex items-center justify-center text-sage-500 font-semibold">{i + 1}</span>
            </button>
          );
        })}
      </div>
      {/* Animated bar between steps */}
      <div className="relative h-1 bg-sage-100 rounded-full overflow-hidden mb-5">
        <div className="absolute inset-y-0 left-0 bg-sage-600 transition-all duration-700" style={{ width: `${((active + 1) / WORKFLOW.length) * 100}%` }} />
      </div>
      <div className="bg-sage-50 border-l-2 border-sage-500 rounded-r-md p-4 animate-fade-in" key={active}>
        <p className="text-[10px] uppercase tracking-wider text-sage-500 font-semibold mb-1">Step {active + 1} of {WORKFLOW.length}</p>
        <p className="font-heading text-lg font-semibold text-sage-900">{WORKFLOW[active].title}</p>
        <p className="text-sm text-sage-700 mt-1">{WORKFLOW[active].body}</p>
      </div>
    </div>
  );
}

// ─────────────────────── Lead form ───────────────────────
function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", problem: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setDone("");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source: "landing",
          utm: Object.fromEntries(new URLSearchParams(window.location.search)),
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Submission failed");
      setDone(j.message || "Thanks!");
      setForm({ name: "", email: "", company: "", problem: "" });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div data-testid="landing-lead-thanks" className="mt-8 max-w-xl mx-auto bg-sage-700 text-cream rounded-lg p-6 text-center">
        <CheckCircle className="w-6 h-6 mx-auto mb-2 text-amber-300" />
        <p className="font-heading text-lg font-semibold">{done}</p>
        <p className="text-sm text-sage-100 mt-1">We've added you to the queue. Watch your inbox.</p>
      </div>
    );
  }
  return (
    <form data-testid="landing-lead-form" onSubmit={submit} className="mt-8 max-w-xl mx-auto bg-white border border-sage-200 rounded-lg p-5 shadow-sm space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input data-testid="lead-input-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm" />
        <input data-testid="lead-input-email" required type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Work email" className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm" />
      </div>
      <input data-testid="lead-input-company" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder="Company / firm (optional)" className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm" />
      <textarea data-testid="lead-input-problem" rows={3} value={form.problem} onChange={e => setForm({ ...form, problem: e.target.value })} placeholder="The one process you wish you could fix this quarter…" className="w-full border border-sage-200 rounded-md px-3 py-2.5 text-sm" />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button data-testid="landing-contact-cta" disabled={busy} type="submit" className="w-full bg-sage-700 text-cream rounded-md px-5 py-3 text-sm font-semibold hover:bg-sage-900 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
        {busy ? "Sending…" : <>Send · we reply in under a week <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

// ─────────────────────── Page ───────────────────────
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-cream font-body text-sage-800">
      {/* Top bar */}
      <header className="relative z-10 border-b border-sage-100 bg-cream/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-sage-700 rounded-md flex items-center justify-center"><Leaf className="w-5 h-5 text-cream" /></div>
            <div>
              <p className="font-heading text-lg font-semibold text-sage-900 leading-none">TheKaizenCabinet</p>
              <p className="text-[11px] text-sage-500 leading-tight">Your Lean Toolbox</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-sage-600">
            <a href="#features" className="hover:text-sage-900">Features</a>
            <a href="#tools" className="hover:text-sage-900">Tools</a>
            <a href="#principles" className="hover:text-sage-900">Principles</a>
            <a href="#contact" className="hover:text-sage-900">Contact</a>
          </nav>
          <Link to="/login" data-testid="landing-login-btn" className="bg-sage-700 text-cream rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-900 transition-colors flex items-center gap-1.5">
            Sign in <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <MeshBg />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-sage-200 rounded-full px-3 py-1 text-xs text-sage-700 mb-5">
            <Sparkles className="w-3 h-3 text-amber-500" /> 39 wizards, one quiet little cabinet
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-sage-900 leading-tight tracking-tight max-w-4xl mx-auto">
            <TypewriterHeadline />
          </h1>
          <p className="mt-6 text-base md:text-lg text-sage-600 max-w-2xl mx-auto leading-relaxed">
            A self-hosted toolbox for consultants and operations leaders — 39 lean wizards,
            comprehensive docs, branded deliverables, and the quiet satisfaction of finally
            having all your favourite tools in one place.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/login" data-testid="hero-cta-primary" className="bg-sage-700 text-cream rounded-md px-5 py-3 text-sm font-semibold hover:bg-sage-900 hover:-translate-y-0.5 hover:shadow-md transition-all flex items-center gap-2">
              Start exploring <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" className="border border-sage-300 text-sage-800 rounded-md px-5 py-3 text-sm font-semibold hover:bg-white transition-all flex items-center gap-2">
              See what's inside
            </a>
          </div>
          <div className="mt-12 flex items-center justify-center gap-6 text-xs text-sage-500">
            <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-sage-600" /> Self-hosted</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-sage-600" /> Whitelabel PDFs</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-sage-600" /> No telemetry</span>
            <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-sage-600" /> 39 tools</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-white border-t border-sage-100">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-2">What's inside the cabinet</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-sage-900 leading-tight">
              Twelve features. One predictable result: your client thinks you're brilliant.
            </h2>
            <p className="mt-3 text-sm text-sage-600">Click any card. There's a punchline on the back.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => <FlipCard key={f.title} f={f} />)}
          </div>
        </div>
      </section>

      {/* Tools vertical tabs */}
      <section id="tools" className="bg-cream border-t border-sage-100">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-2">All 39 tools</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-sage-900 leading-tight">
              Pick a tool. See exactly how you'd use it. And what makes ours different.
            </h2>
          </div>
          <ToolsTabs />
          <div className="mt-6 text-center">
            <Link to="/docs" data-testid="landing-docs-cta" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sage-700 hover:text-sage-900">
              Read full docs for every tool <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section id="principles" className="bg-sage-900 text-cream">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-300 mb-2">Our principles · Our ethics</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold leading-tight max-w-3xl">
            Software you can audit. Data you actually own.
          </h2>
          <p className="mt-3 text-sage-200 max-w-2xl">A toolkit built around three uncomplicated promises.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Server, title: "Self-installed", body: "Runs on your stack — Docker on a laptop, K8s on your VPC. No SaaS lock-in. We provide the bricks; you build the house where you want it." },
              { icon: Lock,   title: "Intranet-friendly", body: "Designed to live behind your firewall. Egress nothing. Block all outbound except your own SMTP and Stripe if you choose to enable it. Your client data stays on your network." },
              { icon: Shield, title: "No telemetry, no tracking", body: "Zero phone-home. No analytics SDKs. No third-party fonts other than the ones you can self-host. The only thing that leaves your server is the deliverable PDFs you choose to send." },
            ].map((p) => {
              const I = p.icon;
              return (
                <div key={p.title} className="bg-sage-800/60 border border-sage-700 rounded-lg p-5">
                  <I className="w-5 h-5 text-amber-300 mb-3" />
                  <p className="font-heading text-lg font-semibold">{p.title}</p>
                  <p className="text-sm text-sage-200 mt-2 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10 bg-sage-800/40 border border-sage-700 rounded-lg p-5 max-w-3xl">
            <p className="font-heading text-base font-semibold mb-2 flex items-center gap-2"><Eye className="w-4 h-4 text-amber-300" /> Why we built it this way</p>
            <p className="text-sm text-sage-200 leading-relaxed">
              Most ops-improvement SaaS platforms quietly export your client's process data to whoever
              owns the cloud account. We didn't want that. The Lean profession runs on trust — your
              clients hand over their messiest, most political problems. We think the tools you use
              should respect that.
            </p>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="bg-white border-t border-sage-100">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-2">How an engagement flows</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-sage-900 leading-tight">
              From kickoff to sustained gains — six steps you can already see.
            </h2>
          </div>
          <AnimatedWorkflow />
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="bg-cream border-t border-sage-100">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center">
            <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage-700 text-cream mb-5"><Mail className="w-5 h-5" /></div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-sage-900 leading-tight">
              Want a copy in your stack?
            </h2>
            <p className="mt-4 text-base text-sage-600 max-w-xl mx-auto">
              Self-installable. We do small, considered rollouts and reply in <strong>under a week</strong>.
              Drop your details and the one process you wish you could fix this quarter.
            </p>
          </div>
          <LeadForm />
          <p className="mt-3 text-center text-xs text-sage-500">
            Prefer email? Write to <a href="mailto:hello@krri.sh" className="underline">hello@krri.sh</a>. No tracking pixels — stored on this server only.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-sage-100">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-sage-700" />
            <p className="text-xs text-sage-500">TheKaizenCabinet — Your Lean Toolbox</p>
          </div>
          <p className="text-xs text-sage-400">© {new Date().getFullYear()} · Self-hosted, intranet-friendly, audit-ready</p>
        </div>
      </footer>
    </div>
  );
}
